import React from "react";
import { useDropzone } from "react-dropzone";

interface UploaderProps {
  fieldKey: string;
  label: string;
  required?: boolean;
  multiple?: boolean;
  maxSizeMB?: number;
  accept?: Record<string, string[]>;
  filesMap: Record<string, File[]>;
  setFilesMap: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
}

export const ReusableUploader: React.FC<UploaderProps> = ({
  fieldKey,
  label,
  required,
  multiple = false,
  maxSizeMB = 5,
  accept = {
    "image/jpeg": [],
    "image/png": [],
    "video/mp4": [],
    "application/pdf": [],
  },
  filesMap,
  setFilesMap,
}) => {
  const handleDrop = (acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      const isValidType = Object.keys(accept).includes(file.type);
      const isValidSize = file.size <= maxSizeMB * 1024 * 1024;

      if (!isValidType) {
        alert(`Invalid file type: ${file.name}`);
        return false;
      }
      if (!isValidSize) {
        alert(`File too large (max ${maxSizeMB}MB): ${file.name}`);
        return false;
      }
      return true;
    });

    setFilesMap((prev) => ({
      ...prev,
      [fieldKey]: multiple
        ? [...(prev[fieldKey] || []), ...validFiles]
        : validFiles.slice(0, 1),
    }));
  };

  const removeFile = (index: number) => {
    setFilesMap((prev) => ({
      ...prev,
      [fieldKey]: prev[fieldKey]?.filter((_, i) => i !== index) || [],
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple,
    accept,
  });

  return (
    <div>
      <label className="block font-medium mb-1">
        {label} {required && "*"}
      </label>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed p-4 rounded-lg cursor-pointer text-center ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the files here...</p>
        ) : (
          <p>
            Drag & drop files here, or{" "}
            <span className="text-blue-600">browse</span>
          </p>
        )}
        <p className="text-xs text-gray-500">
          {Object.keys(accept)
            .map((type) => {
              if (type.startsWith("image/"))
                return type.split("/")[1].toUpperCase();
              if (type.startsWith("video/"))
                return type.split("/")[1].toUpperCase();
              if (type === "application/pdf") return "PDF";
              return type;
            })
            .join(", ")}{" "}
          up to {maxSizeMB}MB
        </p>
      </div>

      {filesMap[fieldKey]?.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {filesMap[fieldKey].map((file, idx) => {
            const fileURL = URL.createObjectURL(file);
            const isImage = file.type.startsWith("image/");
            const isVideo = file.type.startsWith("video/");
            const isPDF = file.type === "application/pdf";

            return (
              <div key={idx} className="relative group">
                {isImage && (
                  <img
                    src={fileURL}
                    alt={file.name}
                    className="w-full h-24 object-cover rounded border"
                  />
                )}
                {isVideo && (
                  <video
                    src={fileURL}
                    controls
                    className="w-full h-24 object-cover rounded border"
                  />
                )}
                {isPDF && (
                  <div className="w-full h-24 flex items-center justify-center border rounded bg-gray-100 text-xs">
                    📄 {file.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded opacity-80 hover:opacity-100"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReusableUploader;
